import { Badge, Button, Card, IconButton, Input, Modal, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Clock, Pencil, Plus, Trash2, Zap } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { FlowResponse, FlowTriggerSummary } from "@/api";
import {
  createFlowTriggerMutation,
  deleteFlowTriggerMutation,
  errorMessage,
  listFlowTriggersQueryKey,
  updateFlowTriggerMutation,
} from "@/api";
import { Text } from "@/components/Text";
import { FlowAuthorizationCard } from "./FlowAuthorizationCard";
import { FlowWebhookSection } from "./FlowWebhookSection";
import {
  DEFAULT_SCHEDULE,
  ScheduleFields,
  type ScheduleValue,
  scheduleToValue,
  valueToSchedule,
} from "./ScheduleFields";

const TRIGGER_TYPES = ["manual", "scheduled"] as const; // webhook is managed in its own section
type TriggerType = (typeof TRIGGER_TYPES)[number];

/**
 * Triggers tab: manual + scheduled triggers (add, edit, delete), the inbound
 * Webhook section, and the background-access authorization card for scheduled
 * runs. Editing a scheduled trigger opens the same form pre-filled.
 */
export function FlowTriggersTab({ flow }: Readonly<{ flow: FlowResponse }>) {
  const { t } = useTranslation("workflows");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<FlowTriggerSummary | null>(null);
  const triggers = (flow.triggers ?? []).filter((tr) => tr.triggerType !== "webhook");

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: listFlowTriggersQueryKey({ path: { flow_uuid: flow.uuid } }) });

  const remove = useMutation({
    ...deleteFlowTriggerMutation(),
    onSuccess: async () => {
      await invalidate();
      toast.success(t("triggers.deleted"));
    },
    onError: (e) => toast.error(errorMessage(e, t("triggers.deleteFailed"))),
  });

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <Text variant="body">{t("triggers.title")}</Text>
            <Button variant="secondary" size="sm" leadingIcon={<Plus size={14} />} onClick={() => setAdding(true)}>
              {t("triggers.add")}
            </Button>
          </div>
          {triggers.length > 0 ? (
            <ul className="flex flex-col divide-y divide-border-subtle">
              {triggers.map((trigger) => (
                <TriggerRow
                  key={trigger.uuid}
                  trigger={trigger}
                  onEdit={() => setEditing(trigger)}
                  onDelete={() => remove.mutate({ path: { flow_uuid: flow.uuid, trigger_uuid: trigger.uuid } })}
                />
              ))}
            </ul>
          ) : (
            <Text variant="body-sm" tone="tertiary">
              {t("triggers.empty")}
            </Text>
          )}
        </div>
      </Card>

      <FlowWebhookSection flow={flow} />
      <FlowAuthorizationCard flow={flow} />

      <TriggerFormModal flow={flow} open={adding} onClose={() => setAdding(false)} onDone={invalidate} />
      {editing && (
        <TriggerFormModal flow={flow} trigger={editing} open onClose={() => setEditing(null)} onDone={invalidate} />
      )}
    </div>
  );
}

function TriggerRow({
  trigger,
  onEdit,
  onDelete,
}: Readonly<{ trigger: FlowTriggerSummary; onEdit: () => void; onDelete: () => void }>) {
  const { t } = useTranslation("workflows");
  const icon = trigger.triggerType === "manual" ? <Zap size={16} /> : <Clock size={16} />;
  const typeLabel =
    trigger.triggerType === "manual" || trigger.triggerType === "scheduled"
      ? t(`triggers.type.${trigger.triggerType}`)
      : trigger.triggerType;
  return (
    <li className="flex items-center gap-3 py-2">
      <span className="text-content-tertiary">{icon}</span>
      <div className="flex min-w-0 flex-1 flex-col">
        <Text variant="body-sm" className="truncate">
          {trigger.name}
        </Text>
        <Text variant="caption" tone="tertiary">
          {typeLabel}
        </Text>
      </div>
      {!trigger.isEnabled && <Badge variant="neutral">{t("status.pending")}</Badge>}
      <IconButton
        icon={<Pencil />}
        aria-label={t("triggers.edit", { name: trigger.name })}
        variant="ghost"
        size="sm"
        onClick={onEdit}
      />
      <IconButton
        icon={<Trash2 />}
        aria-label={t("triggers.delete", { name: trigger.name })}
        variant="ghost"
        size="sm"
        onClick={onDelete}
      />
    </li>
  );
}

function TriggerFormModal({
  flow,
  trigger,
  open,
  onClose,
  onDone,
}: Readonly<{
  flow: FlowResponse;
  trigger?: FlowTriggerSummary;
  open: boolean;
  onClose: () => void;
  onDone: () => Promise<unknown>;
}>) {
  const { t } = useTranslation("workflows");
  const toast = useToast();
  const isEdit = trigger !== undefined;
  const [type, setType] = useState<TriggerType>((trigger?.triggerType as TriggerType) || "scheduled");
  const [name, setName] = useState(trigger?.name ?? "");
  const [schedule, setSchedule] = useState<ScheduleValue>(() =>
    trigger && "schedule" in trigger ? scheduleToValue((trigger as { schedule?: never }).schedule) : DEFAULT_SCHEDULE,
  );

  const create = useMutation(createFlowTriggerMutation());
  const update = useMutation(updateFlowTriggerMutation());
  const saving = create.isPending || update.isPending;

  const submit = async () => {
    const body = {
      name: name.trim(),
      ...(type === "scheduled" ? { schedule: valueToSchedule(schedule) } : {}),
    };
    try {
      if (isEdit) {
        await update.mutateAsync({
          path: { flow_uuid: flow.uuid, trigger_uuid: trigger.uuid },
          body,
          throwOnError: true,
        });
        toast.success(t("triggers.saved"));
      } else {
        await create.mutateAsync({
          path: { flow_uuid: flow.uuid },
          body: { triggerType: type, ...body },
          throwOnError: true,
        });
        toast.success(t("triggers.added"));
      }
      await onDone();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, t(isEdit ? "triggers.saveFailed" : "triggers.addFailed")));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(isEdit ? "triggers.editTitle" : "triggers.addTitle")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("triggers.cancel")}
          </Button>
          <Button variant="primary" loading={saving} disabled={!name.trim()} onClick={submit}>
            {t(isEdit ? "triggers.saveAction" : "triggers.addAction")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {!isEdit && (
          <div className="flex gap-2">
            {TRIGGER_TYPES.map((v) => (
              <Button
                key={v}
                variant={type === v ? "primary" : "secondary"}
                size="sm"
                leadingIcon={v === "manual" ? <Zap size={14} /> : <Clock size={14} />}
                onClick={() => setType(v)}
              >
                {t(`triggers.type.${v}`)}
              </Button>
            ))}
          </div>
        )}
        <Input
          aria-label={t("triggers.name")}
          placeholder={t("triggers.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {type === "scheduled" && <ScheduleFields value={schedule} onChange={setSchedule} />}
        {type === "manual" && (
          <Text variant="body-sm" tone="secondary">
            {t("triggers.manualRunNote")}
          </Text>
        )}
      </div>
    </Modal>
  );
}
